import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Images, X, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { SECTORS, STORE, waLink, sectorMeta } from "../data/store";
import { api, assetUrl } from "../lib/api";

export const WorkGallery = () => {
  const [items, setItems] = useState([]);
  const [sector, setSector] = useState("");
  const [idx, setIdx] = useState(null);

  useEffect(() => { api.listPublic("gallery").then(setItems).catch(() => {}); }, []);

  const list = useMemo(() => items.filter((i) => !sector || i.sector === sector), [items, sector]);
  const current = idx !== null ? list[idx] : null;

  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, list.length]);

  if (items.length === 0) return null;

  return (
    <section id="section-gallery" className="scroll-mt-20 py-16 bg-[#111827] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#F59E0B] font-bold text-xs tracking-widest uppercase mb-2">
              <Images className="w-4 h-4" /> Galería de trabajos
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Obras y proyectos pintados con nuestros productos
            </h2>
            <p className="mt-3 text-gray-300 max-w-xl">Inspirate con trabajos reales de clientes y talleres de Hernandarias y la zona.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            <button data-testid="gallery-filter-todos" onClick={() => setSector("")}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                !sector ? "bg-white text-gray-900 border-white" : "border-white/30 text-gray-200 hover:border-white"}`}>
              Todos
            </button>
            {SECTORS.filter((s) => s.key !== "accesorios").map((s) => (
              <button key={s.key} data-testid={`gallery-filter-${s.key}`} onClick={() => setSector(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  sector === s.key ? "bg-white text-gray-900 border-white" : "border-white/30 text-gray-200 hover:border-white"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="gallery-grid" className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {list.map((g, i) => {
            const meta = sectorMeta(g.sector);
            return (
              <motion.button
                key={g.id} data-testid={`gallery-item-${g.id}`}
                initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 3) * 0.06 }}
                onClick={() => setIdx(i)}
                className={`group relative rounded-2xl overflow-hidden text-left ${g.featured && i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
              >
                <div className={`${g.featured && i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[4/3]"}`}>
                  <img src={assetUrl(g.image_url)} alt={g.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: meta.color, color: "#fff" }}>{meta.label}</span>
                  <div className="font-display font-bold mt-1.5 leading-snug line-clamp-2">{g.title}</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={() => setIdx(null)}
          >
            <button data-testid="lightbox-close" onClick={() => setIdx(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-6 h-6" /></button>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + list.length) % list.length); }}
              className="absolute left-3 sm:left-6 p-2 rounded-full bg-white/10 hover:bg-white/20"><ChevronLeft className="w-6 h-6" /></button>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % list.length); }}
              className="absolute right-3 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20"><ChevronRight className="w-6 h-6" /></button>
            <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={assetUrl(current.image_url)} alt={current.title} className="w-full max-h-[70vh] object-contain rounded-xl" />
              <div className="mt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-[#F59E0B]">{sectorMeta(current.sector).label}</div>
                  <div data-testid="lightbox-title" className="font-display font-bold text-xl">{current.title}</div>
                  <p className="text-gray-300 text-sm mt-1 max-w-xl">{current.description}</p>
                </div>
                <a
                  data-testid="lightbox-whatsapp"
                  href={waLink(`Hola ${STORE.name}, vi el trabajo "${current.title}" en su galería y quiero algo similar. ¿Me asesoran?`)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#1EBE5B] shrink-0"
                >
                  <MessageCircle className="w-4 h-4" /> Quiero algo así
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
